import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  from,
  map,
  mergeMap,
  Observable,
  of,
  Subject,
  toArray,
} from 'rxjs';
import {
  PodcastIframe,
  PodcastItems,
  PodcastPlatforms,
} from '../shared/interfaces/podcast-item.interface';
import { HttpClient } from '@angular/common/http';
import { CaasRecord } from '../shared/interfaces/caas/caas.interface';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class PodcastService implements OnDestroy {
  private platforms: BehaviorSubject<PodcastPlatforms[]> = new BehaviorSubject<
    PodcastPlatforms[]
  >([]);
  private podcasts: BehaviorSubject<PodcastItems[]> = new BehaviorSubject<
    PodcastItems[]
  >([]);
  private iframes: BehaviorSubject<PodcastIframe[]> = new BehaviorSubject<
    PodcastIframe[]
  >([]);
  private destroy$: Subject<void> = new Subject<void>();
  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer
  ) {}

  getPlatforms(): Observable<PodcastPlatforms[]> {
    return this.platforms.asObservable();
  }

  getPodcasts(): Observable<PodcastItems[]> {
    return this.podcasts.asObservable();
  }

  setPlatforms(data: PodcastPlatforms[]) {
    this.platforms.next(data);
  }

  setPodcasts(data: PodcastItems[]) {
    this.podcasts.next(data);
  }

  getPodcastItemsBySerieId(id: string): PodcastItems[] {
    const selectedPodcasts = this.podcasts
      .getValue()
      .filter((podcast: PodcastItems) => podcast.podcastSerieId === id)
      .sort((a, b) => b.timestamp - a.timestamp);
    return selectedPodcasts;
  }

  getSinglePodcastIframe(url: string): Observable<SafeHtml | null> {
    return this.http
      .get<PodcastIframe | null>(
        'https://embed.podigee.com/oembed?url=' + url + '&type=iframe'
      )
      .pipe(
        map((iframe) => {
          if (iframe) {
            return this.sanitizer.bypassSecurityTrustHtml(
              iframe.html.replace('<iframe', '<iframe class="podcast_player"')
            );
          }
          return null;
        }),
        catchError((error) => {
          console.error('Error fetching iframe:', error);
          return of(null); // Return a null or a default value in case of error
        })
      );
  }

  getMultiplePodcastIframes(
    podcastItems: PodcastItems[]
  ): Observable<PodcastItems[]> {
    return from(podcastItems).pipe(
      mergeMap((podcastItem: PodcastItems) =>
        this.getSinglePodcastIframe(podcastItem.url).pipe(
          map((iframe: SafeHtml | null) => {
            if (iframe) {
              return {
                ...podcastItem,
                safeIframe: iframe,
              };
            }
            return podcastItem;
          })
        )
      ),
      toArray()
    );
  }

  getPodcastPlatformBySerieId(id?: string): PodcastPlatforms | undefined {
    const selectedPlatforms = this.platforms
      .getValue()
      .find((platform: PodcastPlatforms) => platform.serieId === id);
    return selectedPlatforms;
  }

  getManualSelectionPodcasts(records: CaasRecord[]): PodcastItems[] {
    const podcastIds = records.map(
      (record: CaasRecord) => record.value.target.identifier
    );
    const podcasts = this.podcasts
      .getValue()
      .filter((podcast: PodcastItems) => podcastIds.includes(podcast.key));
    return podcasts;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
